import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useId } from "../EditActivityContainer";
import type { ActivityUpdatePayload } from "@features/activities/activityTypes";
import { useUpdateActivityMutation } from "@hooks/index";
import { validatePositiveNumber } from "@pages/activityBuilder/modules/moduleUtils";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector } from "@app/hooks";
import { getCapitalizationYears } from "@utils/helperFunctions";
import { initValues } from "../utils";
import { useFirebaseHook, EventTypes } from "@utils/firebaseUtils";
import { useHandleActivityState } from "@pages/activityBuilder/hooks/useHandleActivityState";
import useErrorMessage from "@utils/useErrorMessage";

export const useEditActivity = ({ setIsSuccessModalOpen }: { setIsSuccessModalOpen: (isOpen: boolean) => void }) => {
  const { id } = useId()
  const { project } = useAppSelector(selectCurrentProject);
  const { activity, isFetching, isError, error } = useHandleActivityState();
  const { fireEvent } = useFirebaseHook();
  const [initialvalues, setInitialvalues] =
    useState<ActivityUpdatePayload>(initValues);

  const [
    updateActivity,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateActivityMutation();

  const { errorMsg: blockingError } = useErrorMessage({ isError, error });
  const { errorMsg: updateErrorMessage } = useErrorMessage({
    isError: isUpdateError,
    error: updateError,
  });

  const validationSchema = useMemo(() =>
    Yup.object().shape({
      id: validatePositiveNumber.required(),
      activity: Yup.object().shape({
        name: Yup.string().required("validations.activity_name_required"),
        duration_t2: validatePositiveNumber.required("validations.duration_required").min(1, "validations.duration_min"),
        climate_t2: Yup.string().nullable().required("validations.climate_required"),
        moisture_t2: Yup.string().nullable().required("validations.moisture_required"),
        soc_t2: validatePositiveNumber.nullable(),
        start_year_t2: validatePositiveNumber.nullable().required().test("sy-greater-than-project", "validations.activity_start_year", function (value) {
          const { start_year_t2 } = this.parent;
          return start_year_t2 >= (project?.start_year_of_activities ?? 0);
        }),
        last_year_of_accounting_t2: validatePositiveNumber.nullable().required("validations.last_year_of_accounting_required").test('ly-lower-than-project', 'validations.activity_last_year_of_accounting', function (value) {
          const { last_year_of_accounting_t2 } = this.parent;
          return last_year_of_accounting_t2 <= (project?.last_year_of_accounting ?? 0);
        }).test('ly-greater-than-ip', 'validations.activity_last_year_of_accounting2', function (value) {
          const { last_year_of_accounting_t2, duration_t2, start_year_t2 } = this.parent;
          return last_year_of_accounting_t2 >= duration_t2 + start_year_t2;
        }),
        change_rate: validatePositiveNumber.nullable().required("validations.change_rate_required"),
        cost: validatePositiveNumber.nullable().required().test(
          "cost-grater-than-project",
          "validations.activity_cost_less_than_project",
          function (value) {
            if (project?.cost == null) return true;
            const { cost } = this.parent;
            return cost <= project.cost;
          }
        ),
        capitalization_years: validatePositiveNumber.nullable().min(0, "validations.cap_phase_min"),
      }),
    }), [project])

  useEffect(() => {
    // Guard against stale Redux data from a previously viewed activity
    if (!activity || activity.id !== id) return;

    const duration = activity?.duration_t2 ?? project?.implementation_years
    const startYear = activity?.start_year_t2 ?? project?.start_year_of_activities
    const moisture = activity?.moisture_t2?.id ?? project?.moisture?.id
    const climate = activity?.climate_t2?.id ?? project?.climate?.id
    const lastYearOfAccounting = activity?.last_year_of_accounting_t2 ?? project?.last_year_of_accounting

    const initValues: ActivityUpdatePayload = {
      id: id ?? 0,
      activity: {
        name: activity?.name ?? "",
        duration_t2: duration,
        start_year_t2: startYear,
        last_year_of_accounting_t2: lastYearOfAccounting,
        climate_t2: climate,
        moisture_t2: moisture,
        soc_t2: activity?.soc_t2 ?? null,
        cost: activity?.cost,
        change_rate: activity?.change_rate?.id,
        capitalization_years: getCapitalizationYears(startYear, duration, lastYearOfAccounting),
      },
    };
    setInitialvalues(initValues);
  }, [activity, id, project]);


  const handleSubmit = async (values: ActivityUpdatePayload) => {
    try {
      const payload = {
        ...values,
        activity: {
          ...values.activity,
          duration_t2: values.activity.duration_t2 === project?.implementation_years ? null : values.activity.duration_t2,
          start_year_t2: values.activity.start_year_t2 === project?.start_year_of_activities ? null : values.activity.start_year_t2,
          climate_t2: values.activity.climate_t2 === project?.climate?.id ? null : values.activity.climate_t2,
          moisture_t2: values.activity.moisture_t2 === project?.moisture?.id ? null : values.activity.moisture_t2,
          soc_t2: values.activity.soc_t2,
          last_year_of_accounting_t2: values.activity.last_year_of_accounting_t2 === project?.last_year_of_accounting ? null : values.activity.last_year_of_accounting_t2,
        }
      }
      await updateActivity(payload).unwrap().then(() => fireEvent(EventTypes.edit_activity))
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    activity,
    initialvalues,
    validationSchema,
    handleSubmit,
    isUpdateLoading,
    isUpdateError,
    updateErrorMessage,
    blockingError,
    id,
    isUpdateSuccess,
    isFetching,
  }
}